/**
 * ApplicationLockConflictDialog
 * Modal displayed when user is "kicked out" due to autosave failure from another user's lock.
 */

import * as React from "react";
import {
  AlertCircleIcon,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@porchpass/ui";
import { formatUserDisplay } from "../utils/userUtils";

interface LockedByUser {
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface ApplicationLockConflictDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTakeover: () => Promise<void>;
  lockedByUser: LockedByUser | null;
  isTakingOver?: boolean;
}

export const ApplicationLockConflictDialog: React.FC<
  ApplicationLockConflictDialogProps
> = ({
  open,
  onOpenChange,
  onTakeover,
  lockedByUser,
  isTakingOver = false,
}) => {
  const handleTakeover = () => {
    void (async () => {
      try {
        await onTakeover();
        onOpenChange(false);
      } catch (error) {
        console.error(
          "[ApplicationLockConflictDialog] Takeover failed:",
          error,
        );
      }
    })();
  };

  return (
    <Dialog open={open}>
      <DialogContent>
        <DialogHeader>
          <div className="flex gap-2 items-center">
            <AlertCircleIcon size={20} className="text-[#df1b41]" />
            <DialogTitle>Application Locked by Another User</DialogTitle>
          </div>
        </DialogHeader>
        <DialogBody>
          <p className="text-base text-[#4a5466]">
            This application has been taken over by{" "}
            <strong>{formatUserDisplay(lockedByUser)}</strong>.
          </p>
          <p className="text-base text-[#4a5466] mt-3">
            You can no longer edit this application. You may take over the
            application if needed, which will lock out the other user.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button
            onClick={() => onOpenChange(false)}
            variant="secondary"
            size="s"
            disabled={isTakingOver}
          >
            Close
          </Button>
          <Button
            onClick={handleTakeover}
            disabled={isTakingOver}
            loading={isTakingOver}
            size="s"
          >
            {isTakingOver ? "Taking Over..." : "Take Over"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
